import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { fileUrl } from "@/lib/api";

// Built-in banner; the version tag makes browsers fetch the new picture instead of an old cached one.
const DEFAULT_BANNER = "/images/banner.jpg";
const withVersion = (u) => (u === DEFAULT_BANNER ? `${u}?v=2026-10` : u);

export const Banner = () => {
  const { settings } = useSite();
  const list = (Array.isArray(settings?.banners) && settings.banners.length ? settings.banners : [DEFAULT_BANNER]).map((u) => fileUrl(withVersion(u)));
  const [ratio, setRatio] = useState(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = list.length;
  const idx = i % n;

  useEffect(() => {
    if (n < 2 || paused) return undefined;
    const t = setInterval(() => setI((p) => (p + 1) % n), 6000);
    return () => clearInterval(t);
  }, [n, paused]);

  const go = (d) => setI((p) => (p + d + n) % n);

  return (
    <section data-testid="home-banner" className="px-3 pt-28 md:px-10 md:pt-32">
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{ aspectRatio: ratio || "1942 / 809" }}
        className="group relative mx-auto max-w-[1400px] overflow-hidden rounded-[20px] border border-[#D4AF37]/25 bg-black shadow-[0_30px_80px_-30px_rgba(212,175,55,0.35)] md:rounded-[28px]"
      >
        {list.map((src, k) => (
          <img
            key={`${src}-${k}`}
            src={src}
            alt={`Awa In Kelet banner ${k + 1}`}
            data-testid={`home-banner-img-${k}`}
            onLoad={k === 0 ? (e) => { const { naturalWidth: w, naturalHeight: h } = e.currentTarget; if (w && h) setRatio(`${w} / ${h}`); } : undefined}
            className={`absolute inset-0 h-full w-full object-contain transition-[opacity,transform] duration-[1200ms] ease-out ${k === idx ? "scale-100 opacity-100" : "scale-[1.03] opacity-0"}`}
          />
        ))}
        {n > 1 && (
          <>
            <button data-testid="banner-prev-btn" onClick={() => go(-1)} aria-label="Previous banner" className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-[#0B111E]/60 text-[#F4EEE2] backdrop-blur-md transition-opacity md:opacity-0 md:group-hover:opacity-100">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button data-testid="banner-next-btn" onClick={() => go(1)} aria-label="Next banner" className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-[#0B111E]/60 text-[#F4EEE2] backdrop-blur-md transition-opacity md:opacity-0 md:group-hover:opacity-100">
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2 rounded-full bg-[#0B111E]/50 px-3 py-2 backdrop-blur-md">
              {list.map((_, k) => (
                <button key={k} data-testid={`banner-dot-${k}`} onClick={() => setI(k)} aria-label={`Banner ${k + 1}`} className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${k === idx ? "w-6 bg-[#D4AF37]" : "w-1.5 bg-[#F4EEE2]/60"}`} />
              ))}
            </div>
          </>
        )}
      </motion.div>
    </section>
  );
};
